import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Badge,
  Tabs,
  Tab,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Menu,
  MenuItem,
  Tooltip,
  LinearProgress,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Verified as VerifiedIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CompanyService } from '../../services/companyService';
import { Company, CompanyMember } from '../../types';

interface MyCompaniesModalProps {
  open: boolean;
  onClose: () => void;
}

const AdvancedMyCompaniesModal: React.FC<MyCompaniesModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailedError, setDetailedError] = useState<any>(null);
  const [userRole, setUserRole] = useState<Record<string, string>>({});
  
  // Advanced features state
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyMembers, setCompanyMembers] = useState<CompanyMember[]>([]);
  const [showSpeedDial, setShowSpeedDial] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCompanyForMenu, setSelectedCompanyForMenu] = useState<Company | null>(null);

  useEffect(() => {
    if (open) {
      fetchUserCompanies();
    }
  }, [open]);

  const fetchUserCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      setDetailedError(null);

      console.log('🔍 Starting fetchUserCompanies...');

      // Get current user
      console.log('👤 Getting current user...');
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('❌ Auth error:', authError);
        setError(`Authentication failed: ${authError.message}`);
        setDetailedError({
          type: 'auth_error',
          message: authError.message,
          details: authError,
          timestamp: new Date().toISOString()
        });
        return;
      }

      if (!user) {
        console.error('❌ No user found');
        setError('Please log in to view your companies');
        setDetailedError({
          type: 'no_user',
          message: 'No authenticated user found',
          timestamp: new Date().toISOString()
        });
        return;
      }

      console.log('✅ User found:', { id: user.id, email: user.email });

      // Debug all companies first
      console.log('🔍 Debugging all companies...');
      await CompanyService.debugAllCompanies();

      // Get user's companies
      console.log('🏢 Fetching user companies...');
      const userCompanies = await CompanyService.getUserCompanies(user.id);
      console.log('📊 User companies result:', userCompanies);
      
      if (!userCompanies) {
        console.error('❌ getUserCompanies returned null/undefined');
        setError('Failed to fetch companies - service returned null');
        setDetailedError({
          type: 'service_null',
          message: 'CompanyService.getUserCompanies returned null/undefined',
          user_id: user.id,
          timestamp: new Date().toISOString()
        });
        return;
      }

      setCompanies(userCompanies);
      console.log('✅ Companies set:', userCompanies.length, 'companies');

      // Get user roles for each company
      console.log('👥 Fetching user roles...');
      const roles: Record<string, string> = {};
      for (const company of userCompanies) {
        try {
          console.log(`🔍 Checking role for company: ${company.id} (${company.name})`);
          const { data: memberData, error: memberError } = await supabase
            .from('company_members')
            .select('role')
            .eq('company_id', company.id)
            .eq('user_id', user.id)
            .eq('is_active', true)
            .single();
          
          console.log(`📋 Member data for ${company.name}:`, { memberData, memberError });
          
          if (!memberError && memberData) {
            roles[company.id] = memberData.role;
            console.log(`✅ Role found for ${company.name}: ${memberData.role}`);
          } else {
            console.warn(`⚠️ No role found for ${company.name}:`, memberError);
          }
        } catch (err) {
          console.warn(`❌ Error fetching role for company ${company.id}:`, err);
        }
      }
      setUserRole(roles);
      console.log('✅ Final roles:', roles);

    } catch (err) {
      console.error('💥 Error fetching user companies:', err);
      setError('Failed to load your companies');
      setDetailedError({
        type: 'general_error',
        message: err instanceof Error ? err.message : 'Unknown error',
        error: err,
        stack: err instanceof Error ? err.stack : undefined,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompany = (company: Company) => {
    navigate(`/companies/${company.slug}`);
    onClose();
  };

  const handleEditCompany = (company: Company) => {
    navigate(`/companies/${company.slug}/edit`);
    onClose();
  };

  const handleCompanyDashboard = (company: Company) => {
    navigate(`/dashboard?company=${company.id}&name=${encodeURIComponent(company.name)}`);
    onClose();
  };

  const handleCreateCompany = () => {
    navigate('/companies/create');
    onClose();
  };

  // Advanced feature handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSortBy(event.target.value);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterBy(event.target.value);
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    fetchCompanyMembers(company.id);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, company: Company) => {
    setAnchorEl(event.currentTarget);
    setSelectedCompanyForMenu(company);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCompanyForMenu(null);
  };

  const handleCompanyAction = (action: string) => {
    if (!selectedCompanyForMenu) return;
    
    switch (action) {
      case 'edit':
        navigate(`/companies/${selectedCompanyForMenu.slug}/edit`);
        break;
      case 'view':
        navigate(`/companies/${selectedCompanyForMenu.slug}`);
        break;
      case 'archive':
        // TODO: Implement archive functionality
        console.log('Archive company:', selectedCompanyForMenu.id);
        break;
      case 'delete':
        // TODO: Implement delete functionality
        console.log('Delete company:', selectedCompanyForMenu.id);
        break;
      case 'share':
        // TODO: Implement share functionality
        console.log('Share company:', selectedCompanyForMenu.id);
        break;
      case 'export':
        // TODO: Implement export functionality
        console.log('Export company:', selectedCompanyForMenu.id);
        break;
    }
    handleMenuClose();
  };

  const fetchCompanyMembers = async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('company_members')
        .select(`
          *,
          user:profiles(*)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true);
      
      if (error) throw error;
      setCompanyMembers(data || []);
    } catch (error) {
      console.error('Error fetching company members:', error);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      case 'member': return 'primary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  // Filter and sort companies
  const filteredAndSortedCompanies = companies
    .filter(company => {
      const matchesSearch = company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           company.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = filterBy === 'all' || 
                           (filterBy === 'active' && company.isActive) ||
                           (filterBy === 'inactive' && !company.isActive);
      
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'members':
          return (b.memberIds?.length || 0) - (a.memberIds?.length || 0);
        default:
          return 0;
      }
    });

  // Calculate company statistics
  const getCompanyStats = (company: Company) => {
    return {
      memberCount: company.memberIds?.length || 0,
      isActive: company.isActive,
      createdDate: new Date(company.createdAt).toLocaleDateString(),
      lastUpdated: new Date(company.updatedAt).toLocaleDateString(),
    };
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      case 'member': return 'Member';
      case 'viewer': return 'Viewer';
      default: return 'Unknown';
    }
  };

  // Render different tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Overview
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {filteredAndSortedCompanies.map((company, index) => {
              const stats = getCompanyStats(company);
              const userRoleInCompany = userRole[company.id] || 'member';
              
              return (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      border: '1px solid #e3f2fd',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        borderColor: '#2196f3',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              fontSize: '1.2rem',
                            }}
                          >
                            {company.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                              {company.name}
                            </Typography>
                            <Chip
                              label={getRoleLabel(userRoleInCompany)}
                              color={getRoleColor(userRoleInCompany)}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, company)}
                          size="small"
                          sx={{ color: '#666' }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Box>

                      {company.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {company.description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <GroupIcon fontSize="small" color="primary" />
                          <Typography variant="caption" color="text.secondary">
                            {stats.memberCount} members
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {stats.createdDate}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewCompany(company)}
                          sx={{ flex: 1 }}
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AnalyticsIcon />}
                          onClick={() => handleCompanyDashboard(company)}
                          sx={{ 
                            flex: 1,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            }
                          }}
                        >
                          Dashboard
                        </Button>
                        {(userRoleInCompany === 'owner' || userRoleInCompany === 'admin') && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditCompany(company)}
                            sx={{ flex: 1 }}
                          >
                            Edit
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        );

      case 1: // Analytics
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon color="primary" />
              Company Analytics
            </Typography>
            
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 3,
              }}
            >
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                  {companies.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Companies
                </Typography>
              </Card>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {companies.filter(c => c.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Companies
                </Typography>
              </Card>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {companies.reduce((sum, c) => sum + (c.memberIds?.length || 0), 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Members
                </Typography>
              </Card>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {Object.values(userRole).filter(role => role === 'owner' || role === 'admin').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Admin Roles
                </Typography>
              </Card>
            </Box>
          </Box>
        );

      case 2: // Members
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              Company Members
            </Typography>
            
            {selectedCompany ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedCompany.name} Members
                </Typography>
                <List>
                  {companyMembers.map((member, index) => (
                    <ListItem key={member.id} divider={index < companyMembers.length - 1}>
                      <ListItemAvatar>
                        <Avatar>
                          {member.user?.name?.charAt(0) || 'U'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={member.user?.name || 'Unknown User'}
                        secondary={member.user?.email}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={getRoleLabel(member.role)}
                          color={getRoleColor(member.role)}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography color="text.secondary">
                Select a company to view its members
              </Typography>
            )}
          </Box>
        );

      case 3: // Settings
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon color="primary" />
              Company Settings
            </Typography>
            
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Email notifications for company updates"
              />
              <FormControlLabel
                control={<Switch />}
                label="Auto-archive inactive companies"
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Show company analytics"
              />
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          minHeight: isMobile ? '100vh' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          <Typography variant="h6" component="div">
            My Companies
          </Typography>
          <Badge badgeContent={companies.length} color="secondary" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchUserCompanies}
              sx={{ color: 'white' }}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Advanced Header with Search and Filters */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={handleSortChange}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="members">Members</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Filter"
              value={filterBy}
              onChange={handleFilterChange}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
          
          {/* Tabs for different views */}
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Overview" icon={<BusinessIcon />} />
            <Tab label="Analytics" icon={<AnalyticsIcon />} />
            <Tab label="Members" icon={<PeopleIcon />} />
            <Tab label="Settings" icon={<SettingsIcon />} />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {error}
              </Typography>
              {detailedError && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1, fontSize: '0.75rem' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    🔍 Detailed Error Information:
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    <strong>Type:</strong> {detailedError.type}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    <strong>Message:</strong> {detailedError.message}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    <strong>Timestamp:</strong> {detailedError.timestamp}
                  </Typography>
                  {detailedError.user_id && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      <strong>User ID:</strong> {detailedError.user_id}
                    </Typography>
                  )}
                  {detailedError.details && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      <strong>Details:</strong> {JSON.stringify(detailedError.details, null, 2)}
                    </Typography>
                  )}
                  {detailedError.stack && (
                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      <strong>Stack Trace:</strong><br />
                      {detailedError.stack}
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          ) : companies.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                borderRadius: 3,
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="text.secondary">
                No Companies Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                You haven't joined or created any companies yet. Start by creating your first company!
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateCompany}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Create Company
              </Button>
            </Paper>
          ) : (
            renderTabContent()
          )}

          {/* Create Company Button */}
          {companies.length > 0 && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleCreateCompany}
                sx={{ px: 4, py: 1.5 }}
              >
                Create New Company
              </Button>
            </Box>
          )}
        </Box>

        {/* Speed Dial for Quick Actions */}
        <SpeedDial
          ariaLabel="Company actions"
          sx={{ position: 'absolute', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
          open={showSpeedDial}
          onOpen={() => setShowSpeedDial(true)}
          onClose={() => setShowSpeedDial(false)}
        >
          <SpeedDialAction
            icon={<AddIcon />}
            tooltipTitle="Create Company"
            onClick={handleCreateCompany}
          />
          <SpeedDialAction
            icon={<RefreshIcon />}
            tooltipTitle="Refresh"
            onClick={fetchUserCompanies}
          />
          <SpeedDialAction
            icon={<DownloadIcon />}
            tooltipTitle="Export Data"
            onClick={() => console.log('Export data')}
          />
        </SpeedDial>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleCompanyAction('view')}>
            <ViewIcon sx={{ mr: 1 }} />
            View Company
          </MenuItem>
          <MenuItem onClick={() => handleCompanyAction('edit')}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Company
          </MenuItem>
          <MenuItem onClick={() => handleCompanyAction('share')}>
            <ShareIcon sx={{ mr: 1 }} />
            Share Company
          </MenuItem>
          <MenuItem onClick={() => handleCompanyAction('export')}>
            <DownloadIcon sx={{ mr: 1 }} />
            Export Data
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleCompanyAction('archive')} sx={{ color: 'warning.main' }}>
            <ArchiveIcon sx={{ mr: 1 }} />
            Archive
          </MenuItem>
          <MenuItem onClick={() => handleCompanyAction('delete')} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedMyCompaniesModal;
